/**
 * PlayerMap Component - Visualizes player locations on a Palworld map
 * 
 * Uses the official Palworld mapping formulas that were verified to work correctly.
 */
import React, { useMemo } from 'react';
import type { Player } from '../types';

// Official Palworld map bounds in converted units
const MAP_LEFT = -1922.4;
const MAP_RIGHT = 1234.0;
const MAP_TOP = 1031.1;
const MAP_BOTTOM = -2125.3;

/**
 * Convert Palworld API coordinates to screen percentages.
 * 
 * Uses the official Palworld formulas where:
 * - Data Y (world Y) affects horizontal position (MapX)
 * - Data X (world X) affects vertical position (MapY)
 */
function toScreenPosition(
  worldX: number, 
  worldY: number
): [number, number] {
  // Official Palworld conversion formulas (verified working)
  const mapX = (worldY - 158000) / 459;  // horizontal
  const mapY = (worldX + 123888) / 459;  // vertical
  
  // Convert to percentage (0-1)
  const percentX = (mapX - MAP_LEFT) / (MAP_RIGHT - MAP_LEFT);
  const percentY = (MAP_TOP - mapY) / (MAP_TOP - MAP_BOTTOM);
  
  return [percentX, percentY];
}

interface PlayerPosition {
  player: Player;
  percentX: number;
  percentY: number;
}

interface PlayerMapProps {
  players: Player[];
  mapImageUrl?: string;
}

export function PlayerMap({ players, mapImageUrl }: PlayerMapProps) {
  const playersWithPositions = useMemo(() => {
    if (!players.length) return [];
    
    const positions: PlayerPosition[] = players.map(player => {
      const [percentX, percentY] = toScreenPosition(
        player.location_x, 
        player.location_y
      );
      
      return {
        player,
        percentX,
        percentY,
      };
    });
    
    return positions;
  }, [players]);

  if (!playersWithPositions.length) {
    return (
      <div className="card player-map-card">
        <div className="card-header">
          <h3>World Map</h3>
        </div>
        <div className="empty-state">
          <p>No players online to display on map</p>
        </div>
      </div>
    );
  }

  return (
    <div className="card player-map-card">
      <div className="card-header">
        <h3>World Map</h3>
        <span className="map-info">
          {players.length} player{players.length !== 1 ? 's' : ''} located
        </span>
      </div>
      
      <div className="map-container">
        <div 
          className="map-view" 
          style={{ 
            '--map-image': mapImageUrl ? `url(${mapImageUrl})` : 'none',
          } as React.CSSProperties}
        >
          {!mapImageUrl && (
            <div className="map-grid">
              <div className="grid-lines-x">
                {[...Array(11)].map((_, i) => (
                  <div key={i} className="grid-line" style={{ top: `${(i / 10) * 100}%` }} />
                ))}
              </div>
              <div className="grid-lines-y">
                {[...Array(11)].map((_, i) => (
                  <div key={i} className="grid-line" style={{ left: `${(i / 10) * 100}%` }} />
                ))}
              </div>
            </div>
          )}

          {playersWithPositions.map((pos, index) => (
            <div
              key={pos.player.playerId}
              className="player-marker"
              style={{
                left: `${pos.percentX * 100}%`,
                top: `${pos.percentY * 100}%`,
              }}
              title={`${pos.player.name} (Lv.${pos.player.level})`}
            >
              <div className="player-marker-dot" />
              <div className="player-marker-label" style={{ 
                backgroundColor: index === 0 ? '#3b82f6' : '#8b5cf6' 
              }}>
                {pos.player.name}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="map-legend">
        <div className="legend-coords">
          {playersWithPositions.map((pos, index) => (
            <div key={pos.player.playerId} className="legend-item">
              <span 
                className="legend-dot" 
                style={{ backgroundColor: index === 0 ? '#3b82f6' : '#8b5cf6' }}
              />
              <span className="legend-name">{pos.player.name}</span>
              <span className="legend-position">({pos.player.location_x.toFixed(0)}, {pos.player.location_y.toFixed(0)})</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}